import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { mapTilerKey } from "./Constants";
import JSZip from 'jszip';
import toGeoJSON from '@mapbox/togeojson';
import styles from './KMZViewer.module.css';

// CDN'den yüklenecek maplibre-gl'i kullan
const maplibregl = window.maplibregl;

// Namespace'den bağımsız element bulma helper fonksiyonu
const findElementByLocalName = (parent, localName) => {
  if (!parent) return null;
  const children = parent.children || parent.childNodes || [];
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.localName === localName || child.nodeName === localName || child.tagName === localName) {
      return child;
    }
  }
  return null;
};

const findAllElementsByLocalName = (parent, localName) => {
  if (!parent) return [];
  const results = [];
  const children = parent.children || parent.childNodes || [];
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.localName === localName || child.nodeName === localName || child.tagName === localName) {
      results.push(child);
    }
  }
  return results;
};

// KML'den polygon'ları manuel olarak parse et
const parseKMLPolygons = (kmlDoc) => {
  const polygons = [];
  const placemarks = kmlDoc.querySelectorAll('Placemark');
  
  placemarks.forEach((pm) => {
    // Polygon'ı bul (namespace'den bağımsız)
    let polygon = pm.querySelector('Polygon');
    if (!polygon) {
      polygon = findElementByLocalName(pm, 'Polygon');
    }
    if (!polygon) return;
    
    // OuterBoundaryIs içindeki coordinates'ı bul
    let outerBoundary = polygon.querySelector('outerBoundaryIs');
    if (!outerBoundary) {
      outerBoundary = findElementByLocalName(polygon, 'outerBoundaryIs');
    }
    if (!outerBoundary) {
      outerBoundary = findElementByLocalName(polygon, 'outerBoundary');
    }
    
    if (!outerBoundary) {
      // Eğer outerBoundaryIs yoksa, direkt LinearRing'i ara
      let linearRing = polygon.querySelector('LinearRing');
      if (!linearRing) {
        linearRing = findElementByLocalName(polygon, 'LinearRing');
      }
      if (linearRing) {
        const coords = parseCoordinates(linearRing);
        if (coords && coords.length > 0) {
          const nameEl = pm.querySelector('name');
          const name = nameEl ? nameEl.textContent : 'İsimsiz Polygon';
          const descEl = pm.querySelector('description');
          const styleUrlEl = pm.querySelector('styleUrl');
          const styleUrl = styleUrlEl ? styleUrlEl.textContent : '';
          polygons.push({
            type: 'Feature',
            properties: {
              name: name,
              description: descEl ? descEl.textContent : '',
              styleUrl: styleUrl
            },
            geometry: {
              type: 'Polygon',
              coordinates: [coords]
            }
          });
        }
      }
      return;
    }
    
    let linearRing = outerBoundary.querySelector('LinearRing');
    if (!linearRing) {
      linearRing = findElementByLocalName(outerBoundary, 'LinearRing');
    }
    
    if (!linearRing) return;
    
    const coords = parseCoordinates(linearRing);
    if (!coords || coords.length === 0) return;
    
    // Name ve styleUrl element'lerini bul
    const nameEl = pm.querySelector('name');
    const name = nameEl ? nameEl.textContent : 'İsimsiz Polygon';
    const descEl = pm.querySelector('description');
    const styleUrlEl = pm.querySelector('styleUrl');
    const styleUrl = styleUrlEl ? styleUrlEl.textContent : '';
    
    // Inner boundaries (holes) için
    const innerBoundaries = findAllElementsByLocalName(polygon, 'innerBoundaryIs');
    const holes = [];
    
    innerBoundaries.forEach(inner => {
      let innerRing = inner.querySelector('LinearRing');
      if (!innerRing) {
        innerRing = findElementByLocalName(inner, 'LinearRing');
      }
      if (innerRing) {
        const holeCoords = parseCoordinates(innerRing);
        if (holeCoords && holeCoords.length > 0) {
          holes.push(holeCoords);
        }
      }
    });
    
    const allCoords = [coords, ...holes];
    
    polygons.push({
      type: 'Feature',
      properties: {
        name: name,
        description: descEl ? descEl.textContent : '',
        styleUrl: styleUrl
      },
      geometry: {
        type: 'Polygon',
        coordinates: allCoords
      }
    });
  });
  
  return polygons;
};

// KML coordinates string'ini parse et (longitude,latitude,altitude formatı)
const parseCoordinates = (element) => {
  let coordElement = element.querySelector('coordinates');
  if (!coordElement) {
    coordElement = findElementByLocalName(element, 'coordinates');
  }
  if (!coordElement) return null;
  
  const coordText = coordElement.textContent.trim();
  if (!coordText) return null;
  
  const coords = [];
  // Coordinates boşluk, tab veya newline ile ayrılmış olabilir
  const lines = coordText.split(/[\s\n\r\t]+/).filter(line => line.trim().length > 0);
  
  lines.forEach(line => {
    const trimmed = line.trim();
    if (!trimmed) return;
    
    const parts = trimmed.split(',');
    if (parts.length >= 2) {
      const lng = parseFloat(parts[0].trim());
      const lat = parseFloat(parts[1].trim());
      // Koordinatların mantıklı aralıkta olduğunu kontrol et
      // Türkiye için: longitude 25-45, latitude 35-43
      if (!isNaN(lng) && !isNaN(lat) && lng >= 25 && lng <= 45 && lat >= 35 && lat <= 43) {
        coords.push([lng, lat]);
      }
    }
  });
  
  // Polygon için en az 3 nokta gerekli
  if (coords.length < 3) {
    return null;
  }
  
  // Duplicate noktaları temizle - ardışık aynı noktaları kaldır
  const cleanedCoords = [];
  const tolerance = 0.000001; // Çok küçük tolerans
  
  for (let i = 0; i < coords.length; i++) {
    const current = coords[i];
    const prev = cleanedCoords[cleanedCoords.length - 1];
    
    // İlk nokta veya önceki noktadan farklıysa ekle
    if (!prev || 
        Math.abs(current[0] - prev[0]) >= tolerance || 
        Math.abs(current[1] - prev[1]) >= tolerance) {
      cleanedCoords.push(current);
    }
  }
  
  if (cleanedCoords.length < 3) {
    return null; // Yeterli nokta yok
  }
  
  // Polygon'ı kapat - MapLibre GL için son nokta ilk nokta ile aynı olmalı
  // Ama duplicate olmamalı - eğer zaten kapalıysa, son noktayı kaldır ve tekrar ekle
  const first = cleanedCoords[0];
  const last = cleanedCoords[cleanedCoords.length - 1];
  const isClosed = Math.abs(first[0] - last[0]) < tolerance && Math.abs(first[1] - last[1]) < tolerance;
  
  // Polygon'ı her zaman aynı şekilde kapat - MapLibre GL'nin zoom seviyesine göre değişmemesi için
  // Eğer zaten kapalıysa, son duplicate noktayı kaldır
  if (isClosed && cleanedCoords.length > 3) {
    cleanedCoords.pop();
  }
  
  // Her durumda, polygon'ı kapat (ilk noktayı sona ekle)
  // Bu, MapLibre GL'nin polygon'u doğru render etmesi için gerekli
  // ÖNEMLİ: İlk noktayı tam olarak aynı referansla ekle (yeni array oluşturma)
  cleanedCoords.push([first[0], first[1]]);
  
  // Minimum 4 nokta gerekli (3 köşe + 1 kapalı)
  if (cleanedCoords.length < 4) {
    return null;
  }
  
  return cleanedCoords;
};

const KMZViewer = () => {
  const navigate = useNavigate();
  const map = useRef(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [kmzData, setKmzData] = useState(null);

  useEffect(() => {
    map.current = new maplibregl.Map({
      container: "kmz-map",
      style: "https://api.maptiler.com/maps/basic-v2/style.json?key=" + mapTilerKey,
      center: [32.82, 39.96], // İstanbul merkez
      zoom: 12
    });

    map.current.addControl(new maplibregl.NavigationControl(), 'top-left');
    
    return () => {
      if (map.current) {
        map.current.remove();
      }
    };
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.kmz')) {
      setError('Lütfen bir KMZ dosyası seçin.');
      return;
    }

    setUploadedFile(file);
    setError(null);
    setIsLoading(true);

    try {
      await processKMZFile(file);
    } catch (err) {
      console.error('KMZ işleme hatası:', err);
      setError('KMZ dosyası işlenirken bir hata oluştu: ' + err.message);
      setIsLoading(false);
    }
  };

  const processKMZFile = async (file) => {
    try {
      // KMZ dosyasını zip olarak aç
      const zip = await JSZip.loadAsync(file);
      
      // KML dosyasını bul (genellikle doc.kml veya root seviyesinde .kml dosyası)
      let kmlFile = null;
      let kmlFileName = null;

      // Önce doc.kml'yi kontrol et
      if (zip.files['doc.kml']) {
        kmlFile = zip.files['doc.kml'];
        kmlFileName = 'doc.kml';
      } else {
        // Root seviyesinde .kml dosyası ara
        for (const fileName in zip.files) {
          if (fileName.toLowerCase().endsWith('.kml') && !fileName.includes('/')) {
            kmlFile = zip.files[fileName];
            kmlFileName = fileName;
            break;
          }
        }
      }

      if (!kmlFile) {
        throw new Error('KMZ dosyası içinde KML dosyası bulunamadı.');
      }

      // KML içeriğini oku
      const kmlContent = await kmlFile.async('string');

      // Browser'ın native DOMParser'ını kullan
      const parser = new window.DOMParser();
      const kmlDoc = parser.parseFromString(kmlContent, 'text/xml');

      // Parse hatalarını kontrol et
      const parseError = kmlDoc.querySelector('parsererror');
      if (parseError) {
        throw new Error('KML dosyası parse edilemedi: ' + parseError.textContent);
      }

      // KML'deki style'ları parse et - mavi olanları tespit et
      const styles = kmlDoc.querySelectorAll('Style');
      const styleMap = new Map();
      
      // Style'ları parse et
      styles.forEach(style => {
        const styleId = style.getAttribute('id');
        if (!styleId) return;
        
        // PolyStyle içindeki color'ı bul
        const polyStyle = style.querySelector('PolyStyle') || findElementByLocalName(style, 'PolyStyle');
        if (polyStyle) {
          const colorEl = polyStyle.querySelector('color') || findElementByLocalName(polyStyle, 'color');
          if (colorEl) {
            const colorHex = colorEl.textContent.trim();
            // KML color formatı: AABBGGRR (Alpha, Blue, Green, Red)
            if (colorHex.length >= 8) {
              const alpha = parseInt(colorHex.substring(0, 2), 16);
              const blue = parseInt(colorHex.substring(2, 4), 16);
              const green = parseInt(colorHex.substring(4, 6), 16);
              const red = parseInt(colorHex.substring(6, 8), 16);
              
              // Mavi renk tespiti: Blue değeri yüksek olmalı ve diğerlerinden belirgin şekilde büyük olmalı
              // Daha esnek kontrol: Blue dominant olmalı
              // KML color formatı: AABBGGRR - Blue değeri 2. ve 3. byte'ta
              // Örnek: 8000ffff = alpha:128, blue:255, green:255, red:255 (beyaz ama mavi tonlu)
              // Örnek: ff0000ff = alpha:255, blue:255, green:0, red:0 (tam mavi)
              const isBlue = blue > 150 && blue > green + 50 && blue > red + 50;
              
              // Daha agresif tespit: Eğer blue çok yüksekse (200+) ve diğerleri düşükse
              const isVeryBlue = blue > 200 && green < 150 && red < 150;
              const finalIsBlue = isBlue || isVeryBlue;
              
              // Z_ILCE_PL0 style'ı özel kontrol - bu kırmızı olmalı (yol), mavi değil
              const isZ_ILCE_PL0 = styleId === 'Z_ILCE_PL0';
              const finalIsBlueForZ_ILCE_PL0 = isZ_ILCE_PL0 ? false : finalIsBlue; // Z_ILCE_PL0 asla mavi değil
              
              console.log(`🎨 Style ${styleId}:`, {
                color: colorHex,
                rgba: `rgba(${red}, ${green}, ${blue}, ${alpha/255})`,
                isBlue: finalIsBlueForZ_ILCE_PL0,
                blue: blue,
                green: green,
                red: red,
                isVeryBlue: isVeryBlue,
                isZ_ILCE_PL0: isZ_ILCE_PL0,
                note: isZ_ILCE_PL0 ? 'Z_ILCE_PL0 = YOL (kırmızı), mavi değil!' : ''
              });
              
              styleMap.set('#' + styleId, { 
                color: colorHex, 
                isBlue: finalIsBlueForZ_ILCE_PL0, 
                blue: blue, 
                green: green, 
                red: red,
                alpha: alpha
              });
            }
          }
        }
      });
      
      // StyleMap'leri de kontrol et
      const styleMaps = kmlDoc.querySelectorAll('StyleMap');
      styleMaps.forEach(styleMapEl => {
        const styleMapId = styleMapEl.getAttribute('id');
        if (!styleMapId) return;
        
        // Normal style'ı bul
        const pairs = styleMapEl.querySelectorAll('Pair');
        pairs.forEach(pair => {
          const key = pair.querySelector('key');
          if (key && key.textContent.trim() === 'normal') {
            const styleUrl = pair.querySelector('styleUrl');
            if (styleUrl) {
              const refStyleId = styleUrl.textContent.trim();
              const styleInfo = styleMap.get(refStyleId);
              if (styleInfo) {
                styleMap.set('#' + styleMapId, styleInfo);
              }
            }
          }
        });
      });
      
      // ÖNCE MANUEL PARSE ET - toGeoJSON birleştirme yapabilir, biz orijinal polygon'ları istiyoruz
      // SADECE ORİJİNAL POLYGON ŞEKİLLERİNİ KULLAN - BİRLEŞTİRME YOK
      const manualPolygons = parseKMLPolygons(kmlDoc);
      
      // Manuel parse edilen polygon'lara style bilgisini ekle
      manualPolygons.forEach(polygon => {
        const styleUrl = polygon.properties.styleUrl;
        if (styleUrl && styleUrl.startsWith('#')) {
          const styleInfo = styleMap.get(styleUrl);
          if (styleInfo) {
            polygon.properties.isBlue = styleInfo.isBlue;
            polygon.properties.originalColor = styleInfo.color;
            polygon.properties.styleInfo = styleInfo; // Debug için
          } else {
            // Style bulunamadı, logla
            console.log('⚠️ Style bulunamadı:', styleUrl);
          }
        }
      });
      
      // Mavi polygon sayısını kontrol et
      const bluePolygons = manualPolygons.filter(p => p.properties.isBlue);
      console.log('🔵 Mavi polygon sayısı:', bluePolygons.length);
      if (bluePolygons.length > 0) {
        console.log('🔵 İlk mavi polygon örneği:', bluePolygons[0]);
      }
      
      // toGeoJSON'dan SADECE point ve line'ları al, polygon'ları ASLA kullanma
      // Çünkü toGeoJSON polygon'ları birleştirebilir veya değiştirebilir
      let geoJsonFromToGeoJSON = toGeoJSON.kml(kmlDoc);
      
      // Point ve line'ları toGeoJSON'dan al
      const pointsAndLines = geoJsonFromToGeoJSON.features.filter(f => 
        f.geometry && (
          f.geometry.type === 'Point' || 
          f.geometry.type === 'MultiPoint' ||
          f.geometry.type === 'LineString' || 
          f.geometry.type === 'MultiLineString'
        )
      );
      
      // SADECE manuel parse edilen polygon'ları kullan (birleştirilmemiş, orijinal şekiller)
      // toGeoJSON'dan polygon'ları ASLA kullanma
      const geoJson = {
        type: 'FeatureCollection',
        features: [...manualPolygons, ...pointsAndLines]
      };
      
      console.log('📊 Orijinal polygon sayısı (birleştirilmemiş, manuel parse):', manualPolygons.length);
      const bluePolygonsCount = manualPolygons.filter(p => p.properties.isBlue).length;
      console.log('📊 Mavi polygon sayısı:', bluePolygonsCount);
      console.log('📊 Point ve line sayısı (toGeoJSON\'dan):', pointsAndLines.length);
      
      // Mavi polygon tespiti kontrolü
      if (bluePolygonsCount === 0 && manualPolygons.length > 0) {
        console.warn('⚠️ HİÇ MAVİ POLYGON TESPİT EDİLMEDİ! Style tespiti çalışmıyor olabilir.');
        // İlk birkaç polygon'un styleUrl'lerini göster
        manualPolygons.slice(0, 10).forEach((p, i) => {
          console.log(`  Polygon ${i + 1}:`, {
            name: p.properties.name,
            styleUrl: p.properties.styleUrl,
            styleInfo: p.properties.styleInfo,
            isBlue: p.properties.isBlue
          });
        });
        // StyleMap'teki tüm style'ları göster
        console.log('📋 Tüm style\'lar:', Array.from(styleMap.entries()).slice(0, 10));
      }

      if (!geoJson || !geoJson.features || geoJson.features.length === 0) {
        throw new Error('KML dosyasında görüntülenecek veri bulunamadı.');
      }

      setKmzData(geoJson);
      addToMap(geoJson);
      setIsLoading(false);
    } catch (err) {
      console.error('KMZ işleme hatası:', err);
      throw err;
    }
  };

  const addToMap = (geoJson) => {
    if (!map.current || !map.current.loaded()) {
      map.current.once('load', () => addToMap(geoJson));
      return;
    }

    // Önceki layer'ları ve source'ları temizle
    const sourcesToRemove = [
      'kmz-data', 
      'kmz-polygon-data', 
      'kmz-park-data',
      'kmz-road-data',
      'kmz-other-data',
      'kmz-line-data', 
      'kmz-point-data'
    ];
    const layersToRemove = [
      'kmz-fill-layer', 
      'kmz-fill-outline-layer',
      'kmz-park-fill-layer',
      'kmz-park-outline-layer',
      'kmz-road-fill-layer',
      'kmz-road-outline-layer',
      'kmz-other-outline-layer',
      'kmz-line-layer', 
      'kmz-point-layer'
    ];
    
    layersToRemove.forEach(layerId => {
      if (map.current.getLayer(layerId)) {
        map.current.removeLayer(layerId);
      }
    });
    
    sourcesToRemove.forEach(sourceId => {
      if (map.current.getSource(sourceId)) {
        map.current.removeSource(sourceId);
      }
    });

    // Her geometri tipi için ayrı GeoJSON source'ları oluştur
    let polygonFeatures = geoJson.features.filter(f => 
      f.geometry && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon')
    );
    const lineFeatures = geoJson.features.filter(f => 
      f.geometry && (f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString')
    );
    const pointFeatures = geoJson.features.filter(f => 
      f.geometry && (f.geometry.type === 'Point' || f.geometry.type === 'MultiPoint')
    );

    // Polygon'ları boyutlarına göre filtrele - çok büyük olanları atla
    polygonFeatures = polygonFeatures.filter(f => {
      if (!f.geometry || !f.geometry.coordinates) return false;
      
      const coords = f.geometry.type === 'Polygon' 
        ? f.geometry.coordinates[0] 
        : f.geometry.coordinates[0][0];
      
      if (!coords || coords.length < 3) return false;
      
      const lngs = coords.map(c => c[0]);
      const lats = coords.map(c => c[1]);
      const lngRange = Math.max(...lngs) - Math.min(...lngs);
      const latRange = Math.max(...lats) - Math.min(...lats);
      
      // Çok büyük polygon'ları filtrele - eşiği düşürdük (0.2 derece = yaklaşık 20km)
      // Bu, haritayı kaplayan aşırı büyük polygon'ları filtreler
      const maxRange = 0.2; // 0.2 derece = yaklaşık 20-22 km
      if (lngRange > maxRange || latRange > maxRange) {
        console.log(`🚫 Çok büyük polygon filtrelendi:`, {
          name: f.properties?.name || 'İsimsiz',
          styleUrl: f.properties?.styleUrl || '',
          lngRange: lngRange.toFixed(4),
          latRange: latRange.toFixed(4),
          coords: coords.length
        });
        return false;
      }
      
      return true;
    });
    
    console.log('📏 Filtreleme sonrası polygon sayısı:', polygonFeatures.length);

    // Polygon'ları türlerine göre ayır
    // BASİTLEŞTİRİLMİŞ YAKLAŞIM: Style URL'lerine göre direkt sınıflandır
    const parkPolygons = polygonFeatures.filter(f => {
      const styleUrl = (f.properties?.styleUrl || '').toLowerCase();
      const name = (f.properties?.name || '').toLowerCase();
      
      // Z_ILCE_PL0 style'ı YOL (kırmızı) - park değil!
      if (styleUrl.includes('z_ilce_pl0')) {
        return false; // Z_ILCE_PL0 asla park değil, yol
      }
      
      // StyleUrl'de "YESIL" veya "PL_KA" varsa park
      const isGreenStyle = styleUrl.includes('yesil') || styleUrl.includes('green') || 
                          styleUrl.includes('pl_ka') || styleUrl.includes('mahalle');
      const isParkName = name.includes('park') || name.includes('mahalle');
      
      if (isGreenStyle || isParkName) {
        return true;
      }
      
      return false;
    });
    
    console.log('🌳 Park polygon sayısı:', parkPolygons.length);
    
    // Park polygon'larının boyutlarını kontrol et ve logla
    if (parkPolygons.length > 0) {
      const parkSizes = parkPolygons.slice(0, 5).map(f => {
        if (f.geometry && f.geometry.coordinates) {
          const coords = f.geometry.type === 'Polygon' 
            ? f.geometry.coordinates[0] 
            : f.geometry.coordinates[0][0];
          if (coords && coords.length >= 3) {
            const lngs = coords.map(c => c[0]);
            const lats = coords.map(c => c[1]);
            const lngRange = Math.max(...lngs) - Math.min(...lngs);
            const latRange = Math.max(...lats) - Math.min(...lats);
            return {
              name: f.properties?.name || 'İsimsiz',
              lngRange: lngRange.toFixed(4),
              latRange: latRange.toFixed(4)
            };
          }
        }
        return null;
      }).filter(Boolean);
      console.log('📏 İlk 5 park polygon boyutları:', parkSizes);
    }
    
    const roadPolygons = polygonFeatures.filter(f => {
      // Park olarak işaretlenmişse yol değil
      if (parkPolygons.includes(f)) {
        return false;
      }
      
      const name = (f.properties?.name || '').toLowerCase();
      const styleUrl = (f.properties?.styleUrl || '').toLowerCase();
      
      // Z_ILCE_PL0 style'ı YOL (kırmızı) - özel kontrol
      if (styleUrl.includes('z_ilce_pl0')) {
        // Z_ILCE_PL0 polygon'ları genellikle çok büyük (ilçe sınırları), bunları filtrele
        if (f.geometry && f.geometry.coordinates) {
          const coords = f.geometry.type === 'Polygon' 
            ? f.geometry.coordinates[0] 
            : f.geometry.coordinates[0][0];
          if (coords && coords.length >= 3) {
            const lngs = coords.map(c => c[0]);
            const lats = coords.map(c => c[1]);
            const lngRange = Math.max(...lngs) - Math.min(...lngs);
            const latRange = Math.max(...lats) - Math.min(...lats);
            // Z_ILCE_PL0 polygon'ları çok büyükse filtrele (0.15 derece = yaklaşık 15km)
            if (lngRange > 0.15 || latRange > 0.15) {
              console.log('🚫 Büyük Z_ILCE_PL0 polygon filtrelendi:', {
                name: f.properties?.name || 'İsimsiz',
                lngRange: lngRange.toFixed(4),
                latRange: latRange.toFixed(4)
              });
              return false;
            }
          }
        }
        console.log('🛣️ Z_ILCE_PL0 polygon yol olarak işaretlendi:', f.properties?.name || 'İsimsiz');
        return true;
      }
      
      // StyleUrl'de "YOL" veya kırmızı renk varsa yol
      return styleUrl.includes('yol') || styleUrl.includes('road') || 
             styleUrl.includes('z_ilce') || styleUrl.includes('red') ||
             name.includes('yol') || name.includes('road') || name.includes('street');
    });
    
    console.log('🛣️ Yol polygon sayısı:', roadPolygons.length);
    
    // Yol polygon'larının boyutlarını kontrol et ve logla
    if (roadPolygons.length > 0) {
      const roadSizes = roadPolygons.slice(0, 5).map(f => {
        if (f.geometry && f.geometry.coordinates) {
          const coords = f.geometry.type === 'Polygon' 
            ? f.geometry.coordinates[0] 
            : f.geometry.coordinates[0][0];
          if (coords && coords.length >= 3) {
            const lngs = coords.map(c => c[0]);
            const lats = coords.map(c => c[1]);
            const lngRange = Math.max(...lngs) - Math.min(...lngs);
            const latRange = Math.max(...lats) - Math.min(...lats);
            return {
              name: f.properties?.name || 'İsimsiz',
              styleUrl: f.properties?.styleUrl || '',
              lngRange: lngRange.toFixed(4),
              latRange: latRange.toFixed(4)
            };
          }
        }
        return null;
      }).filter(Boolean);
      console.log('📏 İlk 5 yol polygon boyutları:', roadSizes);
    }
    
    // Diğer polygon'lar (açık yeşil kısımlar) - bunlar boş olacak
    const otherPolygons = polygonFeatures.filter(f => {
      return !parkPolygons.includes(f) && !roadPolygons.includes(f);
    });
    
    console.log('🔍 Polygon ayrımı:', {
      parklar: parkPolygons.length,
      yollar: roadPolygons.length,
      diğer: otherPolygons.length,
      toplam: polygonFeatures.length
    });

    // 1. PARKLAR: Yeşil fill + kırmızı outline (mavi olanlar da buraya dahil - YEŞİL OLACAK)
    if (parkPolygons.length > 0) {
      console.log('🌳 Park layer ekleniyor:', parkPolygons.length, 'polygon');
      console.log('🌳 Mavi park sayısı:', parkPolygons.filter(p => p.properties?.isBlue).length);
      
      const parkGeoJson = {
        type: 'FeatureCollection',
        features: parkPolygons
      };
      map.current.addSource('kmz-park-data', {
        type: 'geojson',
        data: parkGeoJson,
        tolerance: 0, // Basitleştirmeyi tamamen kapat - orijinal şekilleri koru
        generateId: false, // ID üretme
        lineMetrics: false, // Line metrikleri kapalı
        promoteId: false, // ID promosyonu kapalı
        cluster: false, // Clustering kapalı
        clusterRadius: 0 // Cluster radius 0
      });
      
      // Park fill (yeşil) - mavi olanlar da yeşil olacak
      map.current.addLayer({
        id: 'kmz-park-fill-layer',
        type: 'fill',
        source: 'kmz-park-data',
        paint: {
          'fill-color': '#22c55e', // Yeşil (mavi olanlar da yeşil olacak)
          'fill-opacity': 0.5,
          'fill-antialias': false // Antialiasing kapat - daha keskin kenarlar, taşma yok
        },
        minzoom: 0, // Tüm zoom seviyelerinde göster
        maxzoom: 24 // Maksimum zoom
      });
      
      // Park outline (kırmızı) - AYRI LAYER olarak ekle, fill-outline-color kullanma
      // Çünkü fill-outline-color polygon'un dışına taşabilir
      // Line layer'ı polygon'un tam kenarını takip edecek şekilde ayarla
      map.current.addLayer({
        id: 'kmz-park-outline-layer',
        type: 'line',
        source: 'kmz-park-data',
        paint: {
          'line-color': '#ef4444', // Kırmızı
          'line-width': 2,
          'line-opacity': 1,
          'line-join': 'miter', // Keskin köşeler - round yerine
          'line-cap': 'butt', // Düz uçlar - round yerine
          'line-miter-limit': 2 // Miter limit
        },
        layout: {
          'line-cap': 'butt',
          'line-join': 'miter'
        },
        minzoom: 0, // Tüm zoom seviyelerinde göster
        maxzoom: 24 // Maksimum zoom
      });
      
    } else {
      console.warn('⚠️ Park polygon bulunamadı!');
    }
    
    // 2. YOLLAR: Mavi fill + mavi outline
    if (roadPolygons.length > 0) {
      const roadGeoJson = {
        type: 'FeatureCollection',
        features: roadPolygons
      };
      map.current.addSource('kmz-road-data', {
        type: 'geojson',
        data: roadGeoJson,
        tolerance: 0, // Basitleştirmeyi tamamen kapat - orijinal şekilleri koru
        generateId: false, // ID üretme
        lineMetrics: false, // Line metrikleri kapalı
        promoteId: false, // ID promosyonu kapalı
        cluster: false, // Clustering kapalı
        clusterRadius: 0 // Cluster radius 0
      });
      
      // Yol fill (mavi)
      map.current.addLayer({
        id: 'kmz-road-fill-layer',
        type: 'fill',
        source: 'kmz-road-data',
        paint: {
          'fill-color': '#3b82f6', // Mavi
          'fill-opacity': 0.6,
          'fill-antialias': false // Antialiasing kapat - daha keskin kenarlar, taşma yok
        },
        minzoom: 0, // Tüm zoom seviyelerinde göster
        maxzoom: 24 // Maksimum zoom
      });
      
      // Yol outline (koyu mavi) - AYRI LAYER olarak ekle
      map.current.addLayer({
        id: 'kmz-road-outline-layer',
        type: 'line',
        source: 'kmz-road-data',
        paint: {
          'line-color': '#1e40af', // Koyu mavi
          'line-width': 2,
          'line-opacity': 1,
          'line-join': 'miter', // Keskin köşeler - round yerine
          'line-cap': 'butt', // Düz uçlar - round yerine
          'line-miter-limit': 2 // Miter limit
        },
        layout: {
          'line-cap': 'butt',
          'line-join': 'miter'
        },
        minzoom: 0, // Tüm zoom seviyelerinde göster
        maxzoom: 24 // Maksimum zoom
      });
      
    }
    
    // 3. DİĞER POLYGON'LAR: BOŞ (fill yok, hiçbir şey gösterilmiyor)

    // Line layer ekle (mavi)
    if (lineFeatures.length > 0) {
      const lineGeoJson = {
        type: 'FeatureCollection',
        features: lineFeatures
      };
      map.current.addSource('kmz-line-data', {
        type: 'geojson',
        data: lineGeoJson
      });
      map.current.addLayer({
        id: 'kmz-line-layer',
        type: 'line',
        source: 'kmz-line-data',
        paint: {
          'line-color': '#3b82f6', // Mavi (yollar için)
          'line-width': 2,
          'line-opacity': 0.8
        }
      });
    }

    // Point layer ekle
    if (pointFeatures.length > 0) {
      const pointGeoJson = {
        type: 'FeatureCollection',
        features: pointFeatures
      };
      map.current.addSource('kmz-point-data', {
        type: 'geojson',
        data: pointGeoJson
      });
      map.current.addLayer({
        id: 'kmz-point-layer',
        type: 'circle',
        source: 'kmz-point-data',
        paint: {
          'circle-radius': 8,
          'circle-color': '#f59e0b',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2,
          'circle-opacity': 1
        }
      });
    }

    // Haritayı veriye göre zoom yap
    const bounds = new maplibregl.LngLatBounds();
    geoJson.features.forEach(feature => {
      if (feature.geometry.type === 'Point') {
        bounds.extend(feature.geometry.coordinates);
      } else if (feature.geometry.type === 'LineString') {
        feature.geometry.coordinates.forEach(coord => bounds.extend(coord));
      } else if (feature.geometry.type === 'Polygon') {
        feature.geometry.coordinates[0].forEach(coord => bounds.extend(coord));
      } else if (feature.geometry.type === 'MultiPoint') {
        feature.geometry.coordinates.forEach(coord => bounds.extend(coord));
      } else if (feature.geometry.type === 'MultiLineString') {
        feature.geometry.coordinates.forEach(line => {
          line.forEach(coord => bounds.extend(coord));
        });
      } else if (feature.geometry.type === 'MultiPolygon') {
        feature.geometry.coordinates.forEach(polygon => {
          polygon[0].forEach(coord => bounds.extend(coord));
        });
      }
    });

    if (!bounds.isEmpty()) {
      map.current.fitBounds(bounds, {
        padding: 50,
        duration: 1000
      });
    }
  };

  const clearMap = () => {
    if (map.current) {
      const sourcesToRemove = [
        'kmz-data', 
        'kmz-polygon-data', 
        'kmz-park-data',
        'kmz-road-data',
        'kmz-other-data',
        'kmz-line-data', 
        'kmz-point-data'
      ];
      const layersToRemove = [
        'kmz-fill-layer', 
        'kmz-fill-outline-layer',
        'kmz-park-fill-layer',
        'kmz-park-outline-layer',
        'kmz-road-fill-layer',
        'kmz-road-outline-layer',
        'kmz-other-outline-layer',
        'kmz-line-layer', 
        'kmz-point-layer'
      ];
      
      layersToRemove.forEach(layerId => {
        if (map.current.getLayer(layerId)) {
          map.current.removeLayer(layerId);
        }
      });
      
      sourcesToRemove.forEach(sourceId => {
        if (map.current.getSource(sourceId)) {
          map.current.removeSource(sourceId);
        }
      });
    }
    setKmzData(null);
    setUploadedFile(null);
    setError(null);
  };

  return (
    <Fragment>
      <div className={styles.kmzViewerContainer}>
        <div id="kmz-map" className={styles.kmzMap}></div>
        <div className={styles.kmzControls}>
          <h3>KMZ Viewer</h3>
          <div className={styles.uploadSection}>
            <label htmlFor="kmz-file-input" className={styles.uploadButton}>
              {isLoading ? 'Yükleniyor...' : uploadedFile ? uploadedFile.name : 'KMZ Dosyası Seç'}
            </label>
            <input
              id="kmz-file-input"
              type="file"
              accept=".kmz"
              onChange={handleFileUpload}
              className={styles.fileInput}
              disabled={isLoading}
            />
            {uploadedFile && (
              <button onClick={clearMap} className={styles.clearButton}>
                Temizle
              </button>
            )}
          </div>
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}
          {kmzData && (
            <div className={styles.infoSection}>
              <p><strong>Yüklenen:</strong> {uploadedFile.name}</p>
              <p><strong>Feature Sayısı:</strong> {kmzData.features.length}</p>
            </div>
          )}
        </div>
      </div>
      {/* Alt Navigasyon Menüsü */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        background: 'rgba(44, 62, 80, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: '12px 20px',
        gap: '10px',
        flexWrap: 'wrap',
        alignItems: 'center',
        borderRadius: '12px',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
        zIndex: 10000,
        maxWidth: '90%'
      }}>
        <button 
          onClick={() => navigate('/')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#34495e',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'background-color 0.3s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#4a5f7a'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#34495e'}
        >
          🏠 Ana Sayfa
        </button>
        <button 
          onClick={() => navigate('/kmz')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#27ae60',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'background-color 0.3s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#229954'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#27ae60'}
        >
          📍 KMZ Viewer
        </button>
        <button 
          onClick={() => navigate('/kmz-google')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#34495e',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'background-color 0.3s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#4a5f7a'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#34495e'}
        >
          🗺️ Google API ile
        </button>
        <button 
          onClick={() => navigate('/test')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#34495e',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'background-color 0.3s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#4a5f7a'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#34495e'}
        >
          🧪 Test Sayfası
        </button>
      </div>
    </Fragment>
  );
};

export default KMZViewer;
